/**
 * The single authoritative module-readiness registry — both the Dashboard's "Module Readiness" widget and
 * `app/nav.ts` (which nav items are promoted out of "Coming Later," and what status badge they carry) read
 * from this one array. There is deliberately no second hard-coded status list anywhere in the frontend.
 *
 * This is still a small, explicit, human-reviewed mirror of `docs/module-readiness.md` — NOT a live backend
 * health-check registry. When a module's real status changes, update both this file and the doc it mirrors
 * in the same change. See `docs/integrated-modules-1-14-verification.md` for why a hand-maintained mirror
 * was chosen over a live registry (disproportionate to the actual defects found), and
 * `docs/coming-later-feature-assessment.md` for the investigation behind the Module 5/8/9/10/13 rows below.
 *
 * Rows are graded at NAV-ITEM granularity, not strictly one row per numbered module — Module 5 (Time,
 * Attendance, Time Off) and Module 9/10 (Tax Liabilities/Deposits/Filings, Quarterly & Annual Forms) each
 * produce two nav-facing rows, since that's the granularity the actual screens (and the target
 * architecture's own nav labels) are built at.
 */
export type ReadinessLevel = 'ok' | 'caution' | 'blocked';

export interface ModuleReadinessRow {
  /** Module number per docs/epn-assessment/09-epn-target-architecture.md, or null for a cross-cutting
   * feature (like Custom Pay Run) that isn't one of the 14 numbered modules. */
  number: number | null;
  name: string;
  status: string;
  level: ReadinessLevel;
  /** When set, a real screen exists at this path and `nav.ts` promotes this row into the primary nav
   * with a status badge instead of leaving it in "Coming Later." Null = still Coming Later. */
  navPath: string | null;
  /** The nav sidebar label. Defaults to `name` when omitted — set explicitly whenever the nav label is
   * shorter/different from this row's full module-readiness name. */
  navLabel?: string;
}

export const MODULE_READINESS: ModuleReadinessRow[] = [
  { number: 1, name: 'Identity, Tenants, Companies, Permissions', status: 'Available for QA', level: 'ok', navPath: null },
  { number: 2, name: 'Employees & Contractors', status: 'Available for QA', level: 'ok', navPath: null },
  { number: 3, name: 'Payroll Configuration & Pay Schedules', status: 'Available for QA', level: 'ok', navPath: null },
  { number: 4, name: 'Earnings, Deductions, Benefits, Garnishments', status: 'Available for QA — People > Benefits and Deductions screens (catalogs, plans, deduction definitions, elections, bulk assignment). Setup only: payroll calculation is unchanged, and no tax profile is approved for payroll yet, so new plans and deductions need tax review before they can be used', level: 'ok', navPath: null },
  {
    number: 5, name: 'Time & Attendance',
    status: 'Available for QA — daily worked/overtime-hours entry with submit → approve/reject/reopen. No clock-in/out or meal-break capture (see time-and-attendance.md)',
    level: 'ok', navPath: '/time-attendance', navLabel: 'Time & Attendance',
  },
  {
    number: 5, name: 'Time Off',
    status: 'Available for QA — leave plans, enrollments, per-day requests (partial-day native), balances, holiday calendar. Cancellation added this pass; no reasoned manual balance-adjustment yet (see time-off.md)',
    level: 'ok', navPath: '/time-off', navLabel: 'Time Off',
  },
  { number: 6, name: 'Payroll Calculation Engine', status: 'Available in Shadow Mode — Not Approved for Production Payroll', level: 'caution', navPath: null },
  {
    number: 7, name: 'Payroll Review, Approvals, Corrections, Reversals',
    status: 'Available for QA — Payroll Review screen (list, run detail with payroll register, approve / reject with reason). Corrections and reversals are API-only — no screen yet',
    level: 'ok', navPath: '/payroll-review', navLabel: 'Payroll Review',
  },
  { number: null, name: 'Custom Pay Run (guided off-cycle payroll)', status: 'Available for QA — staging only', level: 'ok', navPath: null },
  {
    number: 8, name: 'Payment Processing',
    status: 'Sandbox Simulation Only — No Live Money Movement. Mock ACH provider; no void/reversal path and no bank-account display exist yet (see payments-direct-deposit.md)',
    level: 'caution', navPath: '/payments', navLabel: 'Payments & Direct Deposit',
  },
  {
    number: 9, name: 'Payroll Taxes (liabilities & deposits)',
    status: 'Shadow Tax Calculation — Not Approved for Production Payroll. Aggregates Module 6\'s already-computed liabilities; Mock tax-remittance provider (see payroll-taxes.md)',
    level: 'caution', navPath: '/payroll-taxes', navLabel: 'Payroll Taxes',
  },
  {
    number: 9, name: 'Tax Filings (filings, quarterly & annual forms)',
    status: 'Sandbox Simulation Only — No Agency Submission. Preparer/approver review gate before a Mock tax-filing provider call; no amendment of a Denied filing yet (see tax-filings.md)',
    level: 'caution', navPath: '/tax-filings', navLabel: 'Tax Filings',
  },
  { number: 11, name: 'Accounting Mappings & Reconciliation', status: 'Available for QA — Mock GL provider, no real QuickBooks/NolaPro connection', level: 'caution', navPath: null },
  { number: 12, name: 'Reporting & Documents', status: 'Available for QA', level: 'ok', navPath: null },
  {
    number: 13, name: 'Integrations',
    status: 'Available for QA — read-only status page over the same distributed, Mock-only provider adapters (see integrations.md). No live connection to any external provider exists',
    level: 'caution', navPath: '/integrations', navLabel: 'Integrations',
  },
  { number: 14, name: 'Immutable Audit History', status: 'Available for QA — every module wired into the cross-module Audit History screen', level: 'ok', navPath: null },
  {
    number: null, name: 'Subscriptions & Billing (plans, entitlements, upgrade wizard)',
    status: 'Available for QA — staging only. Simulated billing only, no real payment provider; several pricing-sheet perks are genuinely not built yet and show as Coming Soon (see subscriptions-staging-limitations.md)',
    level: 'caution', navPath: null,
  },
  {
    // navPath stays null even though a real screen exists — "Dashboard" is already a hardcoded
    // STATIC_PRIMARY_NAV entry in nav.ts (the very first sidebar item), so promoting it here too would
    // duplicate it. This row exists purely to document status, mirroring every other capability's entry.
    number: null, name: 'Dashboard (company financial summary — pixel rebuild of the official Escochex dashboard)',
    status: 'Available for QA — staging only. Real payroll/tax/deduction data, standalone shell-less page (see dashboard-architecture.md); no real billing/payment provider anywhere in this module',
    level: 'caution', navPath: null,
  },
];

export const PRODUCTION_PROCESSING_STATUS = 'Production Processing — Prohibited in This Environment';
